import { CronExpression, parseExpression } from 'cron-parser'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { parseCronString } from '@/hooks/useCronSchedule'
import { useGetActiveStudioClients } from '@/lib/react-query'

function countInvocationsForTheInterval(
  startDate: Date,
  endDate: Date,
  interval: CronExpression<true>,
  initialCount = 0,
  limit = 100
): Promise<number> {
  return new Promise((resolve) => {
    try {
      let count = 0
      let nextDate = startDate

      while (nextDate < endDate) {
        nextDate = interval.next().value.toDate()
        count++

        if (count === limit) {
          setTimeout(() => {
            resolve(countInvocationsForTheInterval(startDate, endDate, interval, initialCount + limit, limit))
          }, 0)
          return
        }
      }

      resolve(initialCount + count)
    } catch (err) {
      console.error('Error parsing cron string:', err)
      return 0
    }
  })
}

async function countCronInvocationsInNextDays(cronString: string, days: number): Promise<number> {
  if (cronString === '* * * * *') return 43201
  try {
    const now = new Date()
    const endDate = new Date()
    endDate.setDate(now.getDate() + days)

    const interval = parseExpression(cronString, {
      currentDate: now,
      iterator: true,
    })

    return countInvocationsForTheInterval(now, endDate, interval, 0)
  } catch (err) {
    console.error('Error parsing cron string:', err)
    return 0
  }
}

export const useCronScheduleInvocations = (value: string | undefined, days = 30) => {
  const cronString = parseCronString(value)

  const query = useSuspenseQuery({
    queryKey: ['useCronScheduleInvocations', cronString, days],
    queryFn: () => (cronString ? countCronInvocationsInNextDays(cronString, days) : 0),
  })

  return query.data ?? 0
}

export const useEstimatedCost = (value: string | undefined): [number, string, string] => {
  const invocationsCount = useCronScheduleInvocations(value, 30)
  const { data } = useGetActiveStudioClients()
  const currency = data?.[0]?.price?.currency ?? 'eur'
  const price = parseFloat(data?.[0]?.price?.currency_options?.[currency].unit_amount_decimal ?? '0') / 100
  const totalPrice = (invocationsCount * price).toFixed(2)

  return [price, totalPrice, currency]
}
